import { useState, useEffect, useMemo, Fragment } from "react";
import { Link } from "react-router-dom";
import { APIProvider, Map, Marker } from "@vis.gl/react-google-maps";
import {
  Lock, LogOut, RefreshCw, Search, MapPin, Table as TableIcon,
  CheckCircle2, XCircle, UserPlus, ArrowLeft, Wifi, Trash2,
  ChevronRight, ChevronDown, RadioTower, Ban, Copy, ExternalLink, Home,
  Plus, Pencil, X, Phone, Tv, Tag, ArrowUp, ArrowDown, GripVertical, Eye, EyeOff, Star, AlertTriangle,
} from "lucide-react";
import { ThemeToggle } from "./ThemeContext";

const API = process.env.REACT_APP_BACKEND_URL;
const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const TOKEN_KEY = "briarwood-admin-token";

const COLORS = { covered: "#22C55E", not: "#F59E0B", lead: "#0055FF" };

const card = { background: "var(--surface)", border: "1px solid var(--w10)", borderRadius: 14 };
const FH = "'Space Grotesk', sans-serif";
const FB = "'IBM Plex Sans', sans-serif";
const FM = "'IBM Plex Mono', monospace";

function markerIcon(color) {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><circle cx="12" cy="12" r="7" fill="${color}" stroke="white" stroke-width="2.5"/></svg>`;
  return "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg);
}

function pointColor(row) {
  if (row.type === "signup" || row.type === "waitlist") return COLORS.lead;
  return row.covered ? COLORS.covered : COLORS.not;
}

function Login({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API}/api/admin/login`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) { setError(typeof data.detail === "string" ? data.detail : "Login failed"); return; }
      localStorage.setItem(TOKEN_KEY, data.token);
      onSuccess(data.token);
    } catch (err) { setError("Could not reach the server."); }
    finally { setLoading(false); }
  };

  const inp = { width: "100%", padding: "13px 15px", borderRadius: 10, border: "1px solid var(--w12)", background: "var(--w5)", color: "var(--text)", fontFamily: FB, fontSize: 15, outline: "none", boxSizing: "border-box" };

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ ...card, width: "100%", maxWidth: 400, padding: 36 }}>
        <div style={{ width: 46, height: 46, borderRadius: 12, background: "linear-gradient(135deg,#0055FF,#00E5FF)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 20 }}>
          <Lock size={20} color="#001" strokeWidth={2.4} />
        </div>
        <h1 style={{ fontFamily: FH, fontWeight: 700, fontSize: 24, color: "var(--text)", margin: "0 0 6px", letterSpacing: "-0.02em" }}>Admin Access</h1>
        <p style={{ fontFamily: FB, fontSize: 14, color: "var(--text-muted)", margin: "0 0 24px" }}>Coverage & lead dashboard</p>
        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <input data-testid="admin-email" type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} style={inp} required />
          <input data-testid="admin-password" type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} style={inp} required />
          {error && <p data-testid="admin-login-error" style={{ color: COLORS.not, fontFamily: FB, fontSize: 13, margin: 0 }}>{error}</p>}
          <button data-testid="admin-login-btn" type="submit" disabled={loading} style={{ padding: 14, borderRadius: 10, border: "none", background: "linear-gradient(90deg,#0055FF,#00E5FF)", color: "#001018", fontFamily: FB, fontWeight: 700, fontSize: 15, cursor: loading ? "not-allowed" : "pointer", marginTop: 4 }}>
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </form>
        <Link to="/" style={{ display: "inline-flex", alignItems: "center", gap: 6, marginTop: 20, fontFamily: FB, fontSize: 13, color: "var(--text-muted)", textDecoration: "none" }}>
          <ArrowLeft size={14} /> Back to site
        </Link>
      </div>
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div style={{ ...card, padding: "18px 20px", flex: "1 1 130px", minWidth: 130 }}>
      <div style={{ fontFamily: FH, fontWeight: 700, fontSize: 30, color: color || "var(--text)", lineHeight: 1 }}>{value}</div>
      <div style={{ fontFamily: FM, fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.06em", marginTop: 8 }}>{label}</div>
    </div>
  );
}

function TypeBadge({ row }) {
  if (row.type === "signup" || row.type === "waitlist") {
    return <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px", borderRadius: 100, background: "rgba(0,85,255,0.12)", color: "#0055FF", fontFamily: FM, fontSize: 11, fontWeight: 600 }}><UserPlus size={11} />{row.type === "signup" ? "Signup" : "Waitlist"}</span>;
  }
  if (row.covered) {
    return <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px", borderRadius: 100, background: "rgba(34,197,94,0.14)", color: "#16A34A", fontFamily: FM, fontSize: 11, fontWeight: 600 }}><CheckCircle2 size={11} />Covered</span>;
  }
  return <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px", borderRadius: 100, background: "rgba(245,158,11,0.14)", color: "#D97706", fontFamily: FM, fontSize: 11, fontWeight: 600 }}><XCircle size={11} />Not covered</span>;
}

function fmtDate(iso) {
  try { return new Date(iso).toLocaleString("en-CA", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
}

const TIER_COLORS = { clear: "#22C55E", marginal: "#F59E0B", blocked: "#EF4444", obstructed: "#EF4444" };
function tierColor(tier) { return TIER_COLORS[String(tier || "").toLowerCase()] || "var(--text-muted)"; }
function num(v, d = 2) { return typeof v === "number" ? v.toFixed(d) : "—"; }

function TowerBreakdown({ row }) {
  const towers = Array.isArray(row.tower_results) ? row.tower_results : [];
  const dth = { textAlign: "left", padding: "7px 12px", fontFamily: FM, fontSize: 10, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 600, whiteSpace: "nowrap" };
  const dtd = { padding: "8px 12px", fontFamily: FB, fontSize: 12.5, color: "var(--text)", borderTop: "1px solid var(--w6)", whiteSpace: "nowrap" };
  if (towers.length === 0) {
    return <div data-testid="tower-breakdown-empty" style={{ padding: "14px 18px", fontFamily: FB, fontSize: 13, color: "var(--text-muted)" }}>No tower analysis recorded for this check.</div>;
  }
  return (
    <div data-testid="tower-breakdown" style={{ padding: "6px 4px" }}>
      {row.best_tower && (
        <div style={{ display: "inline-flex", alignItems: "center", gap: 7, margin: "6px 14px 12px", padding: "5px 12px", borderRadius: 100, background: "rgba(0,85,255,0.10)", color: "#0055FF", fontFamily: FM, fontSize: 12, fontWeight: 600 }}>
          <RadioTower size={13} /> Best tower: {row.best_tower}
        </div>
      )}
      <div style={{ overflowX: "auto" }}>
        <table data-testid="tower-results-table" style={{ width: "100%", borderCollapse: "collapse", minWidth: 640 }}>
          <thead><tr>
            <th style={dth}>Tower</th><th style={dth}>Distance</th><th style={dth}>In sector</th>
            <th style={dth}>Served</th><th style={dth}>Tier</th><th style={dth}>Clearance</th>
            <th style={dth}>FSPL</th><th style={dth}>LiDAR</th><th style={dth}>Notes</th>
          </tr></thead>
          <tbody>
            {towers.map((t, i) => (
              <tr key={t.tower_id ?? i} data-testid="tower-result-row">
                <td style={{ ...dtd, fontWeight: 600 }}>{t.tower_name || `Tower ${t.tower_id ?? i}`}</td>
                <td style={dtd}>{typeof t.distance_km === "number" ? `${num(t.distance_km, 2)} km` : "—"}</td>
                <td style={dtd}>{t.in_sector ? <CheckCircle2 size={13} color="#22C55E" /> : <XCircle size={13} color="var(--text-muted)" />}</td>
                <td style={dtd}>{t.served ? <CheckCircle2 size={13} color="#22C55E" /> : <XCircle size={13} color="var(--text-muted)" />}</td>
                <td style={dtd}>{t.tier ? <span style={{ color: tierColor(t.tier), fontWeight: 600, textTransform: "capitalize" }}>{t.tier}</span> : "—"}</td>
                <td style={dtd}>{typeof t.clearance_pct === "number" ? `${num(t.clearance_pct, 1)}%` : "—"}</td>
                <td style={dtd}>{typeof t.fspl_db === "number" ? `${num(t.fspl_db, 1)} dB` : "—"}</td>
                <td style={dtd}>{typeof t.lidar_coverage_pct === "number" && t.lidar_coverage_pct > 0 ? `${num(t.lidar_coverage_pct, 0)}%` : "—"}</td>
                <td style={{ ...dtd, whiteSpace: "normal", maxWidth: 200, color: "var(--text-muted)", fontSize: 12 }}>
                  {t.excluded ? <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Ban size={12} color="#EF4444" />{t.exclusion_reason || "excluded"}</span> : (t.terrain_source || "—")}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function emptyPlan(category) {
  return category === "addon"
    ? { category: "addon", key: "", icon: "phone", name: "", price: "", tagline: "", features: [], active: true, order: 0 }
    : { category: "internet", displaySpeed: "", unit: "Mbps", speed: "", price: "", featured: false, contract: "", tagline: "", features: [], active: true, order: 0 };
}

function PlanForm({ initial, token, onSaved, onClose }) {
  const [form, setForm] = useState({ ...initial, features: (initial.features || []).join("\n") });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const isAddon = form.category === "addon";
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true); setError("");
    const body = {
      ...form,
      features: String(form.features || "").split("\n").map((s) => s.trim()).filter(Boolean),
      order: Number(form.order) || 0,
    };
    if (isAddon && !body.key) body.key = String(body.name || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
    try {
      const url = initial.id ? `${API}/api/admin/plans/${initial.id}` : `${API}/api/admin/plans`;
      const res = await fetch(url, {
        method: initial.id ? "PUT" : "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (!res.ok) { setError("Could not save. Please try again."); return; }
      onSaved();
    } catch (e) { setError("Could not reach the server."); }
    finally { setSaving(false); }
  };

  const lbl = { fontFamily: FM, fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600, marginBottom: 6, display: "block" };
  const inp = { width: "100%", padding: "10px 12px", borderRadius: 9, border: "1px solid var(--w12)", background: "var(--w5)", color: "var(--text)", fontFamily: FB, fontSize: 14, outline: "none", boxSizing: "border-box" };

  return (
    <div data-testid="plan-form-modal" onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "40px 16px", overflowY: "auto" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ ...card, width: "100%", maxWidth: 560, padding: 26 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
          <div style={{ fontFamily: FH, fontWeight: 700, fontSize: 19, letterSpacing: "-0.02em" }}>{initial.id ? "Edit plan" : "New plan"}</div>
          <button data-testid="plan-form-close" onClick={onClose} style={{ background: "transparent", border: "1px solid var(--w10)", borderRadius: 8, width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "var(--text-muted)" }}><X size={16} /></button>
        </div>

        {!initial.id && (
          <div style={{ marginBottom: 16 }}>
            <label style={lbl}>Type</label>
            <div style={{ display: "flex", gap: 8 }}>
              {["internet", "addon"].map((c) => (
                <button key={c} data-testid={"plan-type-" + c} onClick={() => setForm({ ...emptyPlan(c), features: "" })} style={{ flex: 1, padding: "9px 0", borderRadius: 9, border: `1px solid ${form.category === c ? "#0055FF" : "var(--w10)"}`, background: form.category === c ? "rgba(0,85,255,0.12)" : "transparent", color: form.category === c ? "#0055FF" : "var(--text-muted)", fontFamily: FB, fontWeight: 600, fontSize: 13, cursor: "pointer", textTransform: "capitalize" }}>{c === "addon" ? "Add-on" : "Internet"}</button>
              ))}
            </div>
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          {isAddon ? (
            <>
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={lbl}>Name</label>
                <input data-testid="plan-field-name" style={inp} value={form.name || ""} onChange={(e) => set("name", e.target.value)} placeholder="Home Phone" />
              </div>
              <div>
                <label style={lbl}>Icon</label>
                <select data-testid="plan-field-icon" style={inp} value={form.icon || "phone"} onChange={(e) => set("icon", e.target.value)}>
                  <option value="phone">Phone</option>
                  <option value="tv">TV</option>
                </select>
              </div>
              <div>
                <label style={lbl}>Price ($/mo)</label>
                <input data-testid="plan-field-price" style={inp} value={form.price || ""} onChange={(e) => set("price", e.target.value)} placeholder="25" />
              </div>
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={lbl}>Tagline</label>
                <input data-testid="plan-field-tagline" style={inp} value={form.tagline || ""} onChange={(e) => set("tagline", e.target.value)} placeholder="Short description" />
              </div>
            </>
          ) : (
            <>
              <div>
                <label style={lbl}>Speed (display)</label>
                <input data-testid="plan-field-displayspeed" style={inp} value={form.displaySpeed || ""} onChange={(e) => set("displaySpeed", e.target.value)} placeholder="500" />
              </div>
              <div>
                <label style={lbl}>Unit</label>
                <select data-testid="plan-field-unit" style={inp} value={form.unit || "Mbps"} onChange={(e) => set("unit", e.target.value)}>
                  <option value="Mbps">Mbps</option>
                  <option value="Gbps">Gbps</option>
                </select>
              </div>
              <div>
                <label style={lbl}>Speed value (Mbps)</label>
                <input data-testid="plan-field-speed" style={inp} value={form.speed || ""} onChange={(e) => set("speed", e.target.value)} placeholder="500" />
              </div>
              <div>
                <label style={lbl}>Price ($/mo)</label>
                <input data-testid="plan-field-price" style={inp} value={form.price || ""} onChange={(e) => set("price", e.target.value)} placeholder="150" />
              </div>
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={lbl}>Subtitle / tagline</label>
                <input data-testid="plan-field-tagline" style={inp} value={form.tagline || ""} onChange={(e) => set("tagline", e.target.value)} placeholder="500 Mbps symmetric" />
              </div>
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={lbl}>Contract note (leave blank for none)</label>
                <input data-testid="plan-field-contract" style={inp} value={form.contract || ""} onChange={(e) => set("contract", e.target.value)} placeholder="2-Year Contract" />
              </div>
            </>
          )}

          <div style={{ gridColumn: "1 / -1" }}>
            <label style={lbl}>Feature bullets (one per line)</label>
            <textarea data-testid="plan-field-features" style={{ ...inp, minHeight: 96, resize: "vertical", fontFamily: FB }} value={form.features} onChange={(e) => set("features", e.target.value)} placeholder={"Unlimited Data\nLow Latency\nFree Installation"} />
          </div>

          <div>
            <label style={lbl}>Display order</label>
            <input data-testid="plan-field-order" type="number" style={inp} value={form.order} onChange={(e) => set("order", e.target.value)} />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, justifyContent: "flex-end" }}>
            {!isAddon && (
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontFamily: FB, fontSize: 14, color: "var(--text)", cursor: "pointer" }}>
                <input data-testid="plan-field-featured" type="checkbox" checked={!!form.featured} onChange={(e) => set("featured", e.target.checked)} style={{ width: 16, height: 16, cursor: "pointer" }} />
                Featured (Best Value)
              </label>
            )}
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontFamily: FB, fontSize: 14, color: "var(--text)", cursor: "pointer" }}>
              <input data-testid="plan-field-active" type="checkbox" checked={!!form.active} onChange={(e) => set("active", e.target.checked)} style={{ width: 16, height: 16, cursor: "pointer" }} />
              Visible on site
            </label>
          </div>
        </div>

        {error && <p data-testid="plan-form-error" style={{ color: COLORS.not, fontFamily: FB, fontSize: 13, marginTop: 14 }}>{error}</p>}

        <div style={{ display: "flex", gap: 10, marginTop: 22 }}>
          <button data-testid="plan-form-save" onClick={save} disabled={saving} style={{ flex: 1, padding: "12px 0", borderRadius: 10, border: "none", background: "linear-gradient(90deg,#0055FF,#00E5FF)", color: "#001018", fontFamily: FB, fontWeight: 700, fontSize: 15, cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.7 : 1 }}>{saving ? "Saving…" : (initial.id ? "Save changes" : "Create plan")}</button>
          <button data-testid="plan-form-cancel" onClick={onClose} style={{ padding: "12px 20px", borderRadius: 10, border: "1px solid var(--w10)", background: "transparent", color: "var(--text-muted)", fontFamily: FB, fontWeight: 600, fontSize: 14, cursor: "pointer" }}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function PlanRow({ plan, token, onEdit, onChanged }) {
  const [busy, setBusy] = useState(false);
  const Icon = plan.category === "addon" ? (plan.icon === "tv" ? Tv : Phone) : Wifi;
  const title = plan.category === "addon" ? plan.name : `${plan.displaySpeed} ${plan.unit}`;

  const patch = async (fields) => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/admin/plans/${plan.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...plan, ...fields }),
      });
      if (res.ok) onChanged();
    } catch (e) { /* ignore */ }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${title}"? This removes it from the live site.`)) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/admin/plans/${plan.id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) onChanged();
    } catch (e) { /* ignore */ }
    finally { setBusy(false); }
  };

  const iconBtn = { background: "transparent", border: "1px solid var(--w10)", borderRadius: 8, width: 32, height: 32, display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: busy ? "not-allowed" : "pointer", color: "var(--text-muted)", opacity: busy ? 0.5 : 1 };

  return (
    <div data-testid={"plan-row-" + (plan.speed || plan.key)} style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 16px", borderBottom: "1px solid var(--w6)", opacity: plan.active ? 1 : 0.55 }}>
      <div style={{ width: 40, height: 40, borderRadius: 10, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,85,255,0.12)", border: "1px solid rgba(0,85,255,0.28)" }}>
        <Icon size={18} color="#0055FF" strokeWidth={2.3} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontFamily: FH, fontWeight: 700, fontSize: 16 }}>{title}</span>
          {plan.featured && <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 100, background: "rgba(0,229,255,0.14)", color: "#0891b2", fontFamily: FM, fontSize: 10, fontWeight: 600 }}><Star size={10} /> Best Value</span>}
          {!plan.active && <span style={{ padding: "2px 8px", borderRadius: 100, background: "var(--w8)", color: "var(--text-muted)", fontFamily: FM, fontSize: 10, fontWeight: 600 }}>Hidden</span>}
        </div>
        <div style={{ fontFamily: FM, fontSize: 12.5, color: "var(--text-muted)", marginTop: 3 }}>
          ${plan.price}/mo{plan.contract ? ` · ${plan.contract}` : ""}{plan.features?.length ? ` · ${plan.features.length} features` : ""}
        </div>
      </div>
      <button data-testid="plan-toggle-active" title={plan.active ? "Hide from site" : "Show on site"} onClick={() => patch({ active: !plan.active })} disabled={busy} style={iconBtn}>{plan.active ? <Eye size={15} /> : <EyeOff size={15} />}</button>
      <button data-testid="plan-edit-btn" title="Edit" onClick={() => onEdit(plan)} disabled={busy} style={iconBtn}><Pencil size={15} /></button>
      <button data-testid="plan-delete-btn" title="Delete" onClick={remove} disabled={busy} style={{ ...iconBtn, color: COLORS.not }}><Trash2 size={15} /></button>
    </div>
  );
}

function PlansManager({ token, onAuthError }) {
  const [plans, setPlans] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [editing, setEditing] = useState(null); // plan object or null; {} sentinel not used

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const res = await fetch(`${API}/api/admin/plans`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.status === 401) { onAuthError(); return; }
      const d = await res.json();
      setPlans(Array.isArray(d?.plans) ? d.plans : []);
    } catch (e) { setErr("Could not load plans."); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const internet = (plans || []).filter((p) => p.category === "internet");
  const addons = (plans || []).filter((p) => p.category === "addon");

  const sectionHead = (label, count) => (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px", borderBottom: "1px solid var(--w10)" }}>
      <div style={{ fontFamily: FH, fontWeight: 700, fontSize: 15 }}>{label} <span style={{ color: "var(--text-muted)", fontWeight: 400 }}>· {count}</span></div>
    </div>
  );

  return (
    <div data-testid="plans-manager">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 18 }}>
        <p style={{ fontFamily: FB, fontSize: 14, color: "var(--text-muted)", margin: 0 }}>Add, edit, hide or remove the plans shown on your public pricing page.</p>
        <button data-testid="add-plan-btn" onClick={() => setEditing(emptyPlan("internet"))} style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "10px 16px", borderRadius: 10, border: "none", background: "linear-gradient(90deg,#0055FF,#00E5FF)", color: "#001018", fontFamily: FB, fontWeight: 700, fontSize: 14, cursor: "pointer" }}><Plus size={16} /> Add plan</button>
      </div>

      {err && <p style={{ color: COLORS.not, fontFamily: FB }}>{err}</p>}
      {loading && !plans && <p style={{ fontFamily: FB, color: "var(--text-muted)" }}>Loading…</p>}

      {plans && (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          <div style={{ ...card, overflow: "hidden" }}>
            {sectionHead("Internet plans", internet.length)}
            {internet.length === 0 ? <div style={{ padding: 30, textAlign: "center", fontFamily: FB, color: "var(--text-muted)" }}>No internet plans.</div>
              : internet.map((p) => <PlanRow key={p.id} plan={p} token={token} onEdit={setEditing} onChanged={load} />)}
          </div>
          <div style={{ ...card, overflow: "hidden" }}>
            {sectionHead("Add-on services", addons.length)}
            {addons.length === 0 ? <div style={{ padding: 30, textAlign: "center", fontFamily: FB, color: "var(--text-muted)" }}>No add-ons.</div>
              : addons.map((p) => <PlanRow key={p.id} plan={p} token={token} onEdit={setEditing} onChanged={load} />)}
          </div>
        </div>
      )}

      {editing && (
        <PlanForm initial={editing} token={token} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}


function ServiceStatusRow({ service, token, onChanged, onAuthError }) {
  const [status, setStatus] = useState(service.status);
  const [message, setMessage] = useState(service.message || "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const dirty = status !== service.status || message !== (service.message || "");

  const save = async () => {
    setSaving(true); setErr("");
    try {
      const res = await fetch(`${API}/api/admin/service-status/${service.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name: service.name, status, message, order: service.order ?? 0 }),
      });
      if (res.status === 401) { onAuthError(); return; }
      if (!res.ok) { const d = await res.json().catch(() => ({})); setErr(typeof d.detail === "string" ? d.detail : "Could not save."); return; }
      onChanged();
    } catch (e) { setErr("Could not save."); }
    finally { setSaving(false); }
  };

  const sel = { padding: "9px 12px", borderRadius: 9, border: "1px solid var(--w12)", background: "var(--w5)", color: "var(--text)", fontFamily: FB, fontSize: 13, maxWidth: 220, outline: "none" };

  return (
    <div data-testid={`service-row-${service.id}`} style={{ display: "flex", alignItems: "flex-start", gap: 16, padding: "18px 16px", flexWrap: "wrap" }}>
      <div style={{ flex: "0 0 110px", fontFamily: FH, fontWeight: 700, fontSize: 15, paddingTop: 9, display: "flex", alignItems: "center", gap: 8 }}>{service.name === "Phone" ? <Phone size={15} /> : <Tv size={15} />} {service.name}</div>
      <div style={{ flex: "1 1 260px", display: "flex", flexDirection: "column", gap: 10, minWidth: 220 }}>
        <select data-testid="service-status-select" aria-label={`${service.name} status`} value={status} onChange={(e) => setStatus(e.target.value)} style={sel}>
          <option value="operational">Operational</option>
          <option value="degraded">Degraded</option>
          <option value="outage">Outage</option>
        </select>
        <textarea data-testid="service-message-input" aria-label={`${service.name} status message`} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Custom status message shown publicly on /status" rows={2}
          style={{ padding: "9px 12px", borderRadius: 9, border: "1px solid var(--w12)", background: "var(--w5)", color: "var(--text)", fontFamily: FB, fontSize: 13, resize: "vertical", outline: "none" }} />
        {err && <p style={{ color: COLORS.not, fontFamily: FB, fontSize: 12.5, margin: 0 }}>{err}</p>}
      </div>
      <button data-testid="service-save-btn" onClick={save} disabled={!dirty || saving}
        style={{ padding: "10px 18px", borderRadius: 9, border: "none", background: dirty ? "linear-gradient(90deg,#0055FF,#00E5FF)" : "var(--w8)", color: dirty ? "#001018" : "var(--text-muted)", fontFamily: FB, fontWeight: 700, fontSize: 13, cursor: dirty && !saving ? "pointer" : "not-allowed" }}>
        {saving ? "Saving\u2026" : "Save"}
      </button>
 
    </div>
  );
}

function ServicesManager({ token, onAuthError }) {
  const [services, setServices] = useState(null);
  const [err, setErr] = useState("");

  const load = async () => {
    setErr("");
    try {
      const res = await fetch(`${API}/api/admin/service-status`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.status === 401) { onAuthError(); return; }
      const d = await res.json();
      setServices(Array.isArray(d?.services) ? d.services : []);
    } catch (e) { setErr("Could not load service statuses."); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  return (
    <div data-testid="services-manager">
      <p style={{ fontFamily: FB, fontSize: 14, color: "var(--text-muted)", margin: "0 0 18px" }}>
        Status shown on your public Network Status page. Both default to Operational — update the status or add a message if either goes down.
      </p>
      {err && <p style={{ color: COLORS.not, fontFamily: FB }}>{err}</p>}
      {services === null && !err && <p style={{ fontFamily: FB, color: "var(--text-muted)" }}>Loading…</p>}
      {services && (
        <div style={{ ...card, overflow: "hidden" }}>
          {services.length === 0
            ? <div style={{ padding: 30, textAlign: "center", fontFamily: FB, color: "var(--text-muted)" }}>No services configured.</div>
            : services.map((s, i) => (
              <div key={s.id} style={{ borderBottom: i < services.length - 1 ? "1px solid var(--w10)" : "none" }}>
                <ServiceStatusRow service={s} token={token} onChanged={load} onAuthError={onAuthError} />
              </div>
            ))}
        </div>
      )}
    </div>
  );
}


export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [query, setQuery] = useState("");
  const [view, setView] = useState("table");
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState("demand"); // demand | plans | status

  const load = async (tk) => {
    setLoading(true); setErr("");
    try {
      const res = await fetch(`${API}/api/admin/lookups`, { headers: { Authorization: `Bearer ${tk}` } });
      if (res.status === 401) { localStorage.removeItem(TOKEN_KEY); setToken(null); return; }
      const d = await res.json();
      setData(d);
    } catch (e) { setErr("Could not load data."); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (token) load(token); }, [token]);

  const logout = () => { localStorage.removeItem(TOKEN_KEY); setToken(null); setData(null); };

  const [deleting, setDeleting] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());
  const [selected, setSelected] = useState(() => new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const toggleExpanded = (id) => setExpanded((prev) => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const toggleSelected = (id) => setSelected((prev) => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const deleteRow = async (id) => {
    if (!window.confirm("Delete this entry? This cannot be undone.")) return;
    setDeleting(id);
    try {
      const res = await fetch(`${API}/api/admin/lookups/${id}`, {
        method: "DELETE", headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) { localStorage.removeItem(TOKEN_KEY); setToken(null); return; }
      if (res.ok) {
        setData((prev) => prev ? { ...prev, lookups: prev.lookups.filter((x) => x.id !== id) } : prev);
        setSelected((prev) => { const n = new Set(prev); n.delete(id); return n; });
      }
    } catch (e) { /* ignore */ }
    finally { setDeleting(null); }
  };
  const bulkDelete = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (!window.confirm(`Delete ${ids.length} selected ${ids.length === 1 ? "entry" : "entries"}? This cannot be undone.`)) return;
    setBulkDeleting(true);
    try {
      const res = await fetch(`${API}/api/admin/lookups/bulk-delete`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ids }),
      });
      if (res.status === 401) { localStorage.removeItem(TOKEN_KEY); setToken(null); return; }
      if (res.ok) {
        setData((prev) => prev ? { ...prev, lookups: prev.lookups.filter((x) => !selected.has(x.id)) } : prev);
        setSelected(new Set());
      }
    } catch (e) { /* ignore */ }
    finally { setBulkDeleting(false); }
  };

  const rows = data?.lookups || [];
  const stats = useMemo(() => {
    const checks = rows.filter((x) => x.type === "coverage_check");
    const covered = checks.filter((x) => x.covered).length;
    return {
      total: rows.length,
      coverage_checks: checks.length,
      covered,
      not_covered: checks.length - covered,
      leads: rows.filter((x) => x.type === "signup" || x.type === "waitlist").length,
    };
  }, [rows]);
  const filtered = useMemo(() => {
    let r = rows;
    if (filter === "covered") r = r.filter((x) => x.type === "coverage_check" && x.covered);
    else if (filter === "not") r = r.filter((x) => x.type === "coverage_check" && !x.covered);
    else if (filter === "leads") r = r.filter((x) => x.type === "signup" || x.type === "waitlist");
    if (query.trim()) {
      const q = query.toLowerCase();
      r = r.filter((x) => [x.address, x.name, x.email, x.phone].filter(Boolean).some((v) => String(v).toLowerCase().includes(q)));
    }
    return r;
  }, [rows, filter, query]);

  const mapPoints = filtered.filter((x) => typeof x.lat === "number" && typeof x.lng === "number");

  if (!token) return <Login onSuccess={setToken} />;

  const th = { textAlign: "left", padding: "12px 16px", fontFamily: FM, fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600, borderBottom: "1px solid var(--w10)", whiteSpace: "nowrap" };
  const td = { padding: "13px 16px", fontFamily: FB, fontSize: 13, color: "var(--text)", borderBottom: "1px solid var(--w6)", verticalAlign: "top" };
  const tab = (active) => ({ display: "inline-flex", alignItems: "center", gap: 7, padding: "8px 15px", borderRadius: 9, border: "1px solid var(--w10)", background: active ? "var(--w8)" : "transparent", color: active ? "var(--text)" : "var(--text-muted)", fontFamily: FB, fontSize: 13, fontWeight: 600, cursor: "pointer" });
  const chip = (active, color) => ({ padding: "6px 13px", borderRadius: 100, border: `1px solid ${active ? color : "var(--w10)"}`, background: active ? `${color}22` : "transparent", color: active ? color : "var(--text-muted)", fontFamily: FB, fontSize: 12, fontWeight: 600, cursor: "pointer" });

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg)", color: "var(--text)" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "28px 24px 60px" }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 14, marginBottom: 26 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
            <div style={{ width: 36, height: 36, borderRadius: 9, background: "linear-gradient(135deg,#0055FF,#00E5FF)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Wifi size={18} color="#001" strokeWidth={2.6} />
            </div>
            <div>
              <div style={{ fontFamily: FH, fontWeight: 700, fontSize: 20, letterSpacing: "-0.02em" }}>Demand Dashboard</div>
              <div style={{ fontFamily: FM, fontSize: 11, color: "var(--text-muted)" }}>Coverage checks & leads</div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button data-testid="admin-refresh" onClick={() => load(token)} style={{ ...tab(false), padding: 10 }} title="Refresh"><RefreshCw size={15} className={loading ? "spin" : ""} /></button>
            <ThemeToggle />
            <button data-testid="admin-logout" onClick={logout} style={{ ...tab(false) }}><LogOut size={15} /> Logout</button>
          </div>
        </div>

        {/* Page nav */}
        <div style={{ display: "flex", gap: 8, marginBottom: 24, borderBottom: "1px solid var(--w8)", paddingBottom: 2 }}>
          <button data-testid="nav-demand" onClick={() => setPage("demand")} style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "10px 16px", border: "none", borderBottom: `2px solid ${page === "demand" ? "#0055FF" : "transparent"}`, background: "transparent", color: page === "demand" ? "var(--text)" : "var(--text-muted)", fontFamily: FB, fontSize: 14, fontWeight: 600, cursor: "pointer", marginBottom: -3 }}><TableIcon size={15} /> Demand</button>
          <button data-testid="nav-plans" onClick={() => setPage("plans")} style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "10px 16px", border: "none", borderBottom: `2px solid ${page === "plans" ? "#0055FF" : "transparent"}`, background: "transparent", color: page === "plans" ? "var(--text)" : "var(--text-muted)", fontFamily: FB, fontSize: 14, fontWeight: 600, cursor: "pointer", marginBottom: -3 }}><Tag size={15} /> Plans</button>
          <button data-testid="nav-status" onClick={() => setPage("status")} style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "10px 16px", border: "none", borderBottom: `2px solid ${page === "status" ? "#0055FF" : "transparent"}`, background: "transparent", color: page === "status" ? "var(--text)" : "var(--text-muted)", fontFamily: FB, fontSize: 14, fontWeight: 600, cursor: "pointer", marginBottom: -3 }}><AlertTriangle size={15} /> Status</button>
        </div>

        {page === "plans" && (
          <PlansManager token={token} onAuthError={() => { localStorage.removeItem(TOKEN_KEY); setToken(null); }} />
        )}

        {page === "status" && (
          <ServicesManager token={token} onAuthError={() => { localStorage.removeItem(TOKEN_KEY); setToken(null); }} />
        )}

        {page === "demand" && (<>
        {/* Stats */}
        {data && (
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 22 }}>
            <StatCard label="Total entries" value={stats.total} />
            <StatCard label="Coverage checks" value={stats.coverage_checks} />
            <StatCard label="Covered" value={stats.covered} color={COLORS.covered} />
            <StatCard label="Not covered" value={stats.not_covered} color={COLORS.not} />
            <StatCard label="Leads" value={stats.leads} color={COLORS.lead} />
          </div>
        )}

        {/* Controls */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 16 }}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button data-testid="filter-all" onClick={() => setFilter("all")} style={chip(filter === "all", "#0055FF")}>All</button>
            <button data-testid="filter-covered" onClick={() => setFilter("covered")} style={chip(filter === "covered", COLORS.covered)}>Covered</button>
            <button data-testid="filter-not" onClick={() => setFilter("not")} style={chip(filter === "not", COLORS.not)}>Not covered</button>
            <button data-testid="filter-leads" onClick={() => setFilter("leads")} style={chip(filter === "leads", COLORS.lead)}>Leads</button>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <div style={{ position: "relative" }}>
              <Search size={15} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)" }} />
              <input data-testid="admin-search" placeholder="Search address, name, email…" value={query} onChange={(e) => setQuery(e.target.value)} style={{ padding: "9px 12px 9px 34px", borderRadius: 9, border: "1px solid var(--w10)", background: "var(--w5)", color: "var(--text)", fontFamily: FB, fontSize: 13, outline: "none", width: 240 }} />
            </div>
            <button data-testid="view-table" onClick={() => setView("table")} style={tab(view === "table")}><TableIcon size={15} /> Table</button>
            <button data-testid="view-map" onClick={() => setView("map")} style={tab(view === "map")}><MapPin size={15} /> Map</button>
          </div>
        </div>

        {err && <p style={{ color: COLORS.not, fontFamily: FB }}>{err}</p>}

        {/* Bulk action bar */}
        {view === "table" && selected.size > 0 && (
          <div data-testid="bulk-action-bar" style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14, padding: "10px 16px", borderRadius: 10, background: "var(--w5)", border: "1px solid var(--w10)" }}>
            <span style={{ fontFamily: FM, fontSize: 13, fontWeight: 600, color: "var(--text)" }}>{selected.size} selected</span>
            <button data-testid="bulk-delete-btn" onClick={bulkDelete} disabled={bulkDeleting} style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "7px 14px", borderRadius: 9, border: `1px solid ${COLORS.not}`, background: `${COLORS.not}18`, color: COLORS.not, fontFamily: FB, fontSize: 13, fontWeight: 600, cursor: bulkDeleting ? "not-allowed" : "pointer", opacity: bulkDeleting ? 0.6 : 1 }}>
              <Trash2 size={14} /> {bulkDeleting ? "Deleting…" : `Delete ${selected.size}`}
            </button>
            <button data-testid="bulk-clear-btn" onClick={() => setSelected(new Set())} style={{ padding: "7px 12px", borderRadius: 9, border: "1px solid var(--w10)", background: "transparent", color: "var(--text-muted)", fontFamily: FB, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Clear</button>
          </div>
        )}

        {/* Table view */}
        {view === "table" && (
          <div style={{ ...card, overflow: "hidden" }}>
            <div style={{ overflowX: "auto" }}>
              <table data-testid="lookups-table" style={{ width: "100%", borderCollapse: "collapse", minWidth: 720 }}>
                <thead><tr>
                  <th style={{ ...th, width: 40 }}>
                    <input
                      type="checkbox"
                      data-testid="select-all-checkbox"
                      checked={filtered.length > 0 && filtered.every((r) => selected.has(r.id))}
                      ref={(el) => { if (el) el.indeterminate = filtered.some((r) => selected.has(r.id)) && !filtered.every((r) => selected.has(r.id)); }}
                      onChange={(e) => {
                        if (e.target.checked) setSelected((prev) => { const n = new Set(prev); filtered.forEach((r) => n.add(r.id)); return n; });
                        else setSelected((prev) => { const n = new Set(prev); filtered.forEach((r) => n.delete(r.id)); return n; });
                      }}
                      style={{ cursor: "pointer", width: 15, height: 15 }}
                    />
                  </th>
                  <th style={th}>Type</th><th style={th}>Address</th><th style={th}>Tower</th>
                  <th style={th}>Contact</th><th style={th}>When</th><th style={{ ...th, textAlign: "right" }}></th>
                </tr></thead>
                <tbody>
                  {filtered.map((r) => {
                    const isCheck = r.type === "coverage_check";
                    const towerCount = Array.isArray(r.tower_results) ? r.tower_results.length : 0;
                    const canExpand = isCheck && (towerCount > 0 || r.best_tower);
                    const isOpen = expanded.has(r.id);
                    const isSel = selected.has(r.id);
                    return (
                    <Fragment key={r.id}>
                    <tr data-testid="lookup-row" style={isSel ? { background: "var(--w4)" } : undefined}>
                      <td style={{ ...td, textAlign: "center" }}>
                        <input
                          type="checkbox"
                          data-testid="select-row-checkbox"
                          checked={isSel}
                          onChange={() => toggleSelected(r.id)}
                          style={{ cursor: "pointer", width: 15, height: 15 }}
                        />
                      </td>
                      <td style={td}><TypeBadge row={r} /></td>
                      <td style={{ ...td, minWidth: 220 }}>
                        {r.address || <span style={{ color: "var(--text-muted)" }}>—</span>}
                        {typeof r.lat === "number" && typeof r.lng === "number" && (
                          <div data-testid="lookup-coords" style={{ marginTop: 4, display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ fontFamily: FM, fontSize: 11.5, color: "var(--text-muted)" }}>{r.lat.toFixed(6)}, {r.lng.toFixed(6)}</span>
                            <button
                              data-testid="copy-coords-btn"
                              onClick={() => navigator.clipboard && navigator.clipboard.writeText(`${r.lat},${r.lng}`)}
                              title="Copy coordinates"
                              style={{ background: "transparent", border: "1px solid var(--w10)", borderRadius: 6, padding: "1px 5px", cursor: "pointer", color: "var(--text-muted)", display: "inline-flex", alignItems: "center" }}
                            ><Copy size={11} /></button>
                            <a
                              data-testid="maps-coords-link"
                              href={`https://www.google.com/maps/search/?api=1&query=${r.lat},${r.lng}`}
                              target="_blank" rel="noreferrer"
                              title="Open in Google Maps"
                              style={{ color: "#0055FF", display: "inline-flex", alignItems: "center" }}
                            ><ExternalLink size={11} /></a>
                          </div>
                        )}
                        {r.rooftop_snapped && r.rooftop_offset_m > 0 && (
                          <div data-testid="rooftop-badge" title="Checked at the building rooftop centre (from Microsoft building footprints) instead of the front-of-lot geocode — antennas mount on the roof, which usually clears front-yard trees." style={{ marginTop: 5, display: "inline-flex", alignItems: "center", gap: 5, padding: "2px 8px", borderRadius: 100, background: "rgba(0,85,255,0.12)", color: "#0055FF", fontFamily: FM, fontSize: 10.5, fontWeight: 600 }}>
                            <Home size={11} /> Snapped to rooftop (~{r.rooftop_offset_m} m from pin)
                          </div>
                        )}
                      </td>
                      <td style={td}>
                        {canExpand ? (
                          <button
                            data-testid="tower-expand-btn"
                            onClick={() => toggleExpanded(r.id)}
                            style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "transparent", border: "1px solid var(--w10)", borderRadius: 8, padding: "5px 10px", cursor: "pointer", color: "var(--text)", fontFamily: FB, fontSize: 12.5, fontWeight: 600 }}
                          >
                            {isOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                            <RadioTower size={13} color="#0055FF" />
                            {r.best_tower || r.served_by || "View"}
                            {towerCount > 0 && <span style={{ color: "var(--text-muted)", fontWeight: 400 }}>· {towerCount}</span>}
                          </button>
                        ) : (r.served_by || <span style={{ color: "var(--text-muted)" }}>—</span>)}
                      </td>
                      <td style={td}>
                        {r.name ? (
                          <div style={{ lineHeight: 1.5 }}>
                            <div style={{ fontWeight: 600 }}>{r.name}</div>
                            {r.email && <div style={{ color: "var(--text-muted)", fontSize: 12 }}>{r.email}</div>}
                            {r.phone && <div style={{ color: "var(--text-muted)", fontSize: 12 }}>{r.phone}</div>}
                            {r.plan_speed && <div style={{ color: "#0055FF", fontSize: 12 }}>{r.plan_speed} · {r.plan_price}</div>}
                          </div>
                        ) : <span style={{ color: "var(--text-muted)" }}>—</span>}
                      </td>
                      <td style={{ ...td, whiteSpace: "nowrap", color: "var(--text-muted)", fontFamily: FM, fontSize: 12 }}>{fmtDate(r.created_at)}</td>
                      <td style={{ ...td, textAlign: "right" }}>
                        <button data-testid="delete-lookup-btn" onClick={() => deleteRow(r.id)} disabled={deleting === r.id} title="Delete entry" style={{ background: "transparent", border: "1px solid var(--w10)", borderRadius: 8, width: 32, height: 32, display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: deleting === r.id ? "not-allowed" : "pointer", color: COLORS.not, opacity: deleting === r.id ? 0.5 : 1 }}>
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr data-testid="tower-detail-row">
                        <td colSpan={7} style={{ padding: 0, background: "var(--w4)", borderBottom: "1px solid var(--w6)" }}>
                          <TowerBreakdown row={r} />
                        </td>
                      </tr>
                    )}
                    </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {filtered.length === 0 && !loading && (
              <div style={{ padding: 50, textAlign: "center", fontFamily: FB, color: "var(--text-muted)" }}>No entries yet.</div>
            )}
          </div>
        )}

        {/* Map view */}
        {view === "map" && (
          <div style={{ ...card, overflow: "hidden", height: 560 }}>
            {MAPS_KEY ? (
              <APIProvider apiKey={MAPS_KEY}>
                <Map defaultCenter={{ lat: 42.40, lng: -82.19 }} defaultZoom={9} gestureHandling="greedy" disableDefaultUI={false} style={{ width: "100%", height: "100%" }}>
                  {mapPoints.map((p) => (
                    <Marker key={p.id} position={{ lat: p.lat, lng: p.lng }} title={`${p.address || ""}`} icon={markerIcon(pointColor(p))} />
                  ))}
                </Map>
              </APIProvider>
            ) : <div style={{ padding: 50, textAlign: "center", color: "var(--text-muted)", fontFamily: FB }}>Map unavailable (missing Google Maps key).</div>}
          </div>
        )}

        {view === "map" && (
          <div style={{ display: "flex", gap: 18, marginTop: 14, fontFamily: FB, fontSize: 12, color: "var(--text-muted)", flexWrap: "wrap" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ width: 11, height: 11, borderRadius: "50%", background: COLORS.covered }} /> Covered</span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ width: 11, height: 11, borderRadius: "50%", background: COLORS.not }} /> Not covered</span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ width: 11, height: 11, borderRadius: "50%", background: COLORS.lead }} /> Lead (signup/waitlist)</span>
            <span>· {mapPoints.length} mapped</span>
          </div>
        )}
        </>)}
      </div>
    </div>
  );
}
